/**
 * @file RegionsListSelector/component.js
 * @copyright 2022 CBC/Radio-Canada. All rights reserved.
 *
 */

import { useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import IconChevron from "../../../Icon/IconChevron";

import LinkWrap from "../../../LinkWrap/component";
import { trackToggleButtonClick } from "../../../../actions/Tracking/utils";
import useLocalNewsRegion from "../../../../hooks/useLocalNewsRegion";

import * as CONSTANTS from "./constants";
import { REGIONS_LIST } from "../../../../constants/regions";
import { Heading } from "../../../Heading/component";
export const RegionsListSelector = ({
    placement = "",
    className,
    useDefault = true,
}) => {
    const { region, update, isExplicitlySet } = useLocalNewsRegion();
    const [menuOpen, setMenuOpen] = useState(false);
    const [selectedRegion, setSelectedRegion] = useState(
        useDefault || isExplicitlySet ? region.preference : null
    );

    // Sets selected region based on user selection
    const handleRegionSelection = (event) => {
        const selection = event.currentTarget.dataset.preference;
        setSelectedRegion(selection);
    };

    // Updates the state of the dropdown to indicate open or close status
    const toggleDropdownVisibility = () => {
        trackToggleButtonClick(`choose region menu`, menuOpen, placement); // Amplitude tracking
        setMenuOpen(!menuOpen);
    };

    // Updates selected region and closes the dropdown
    const handleConfirmSelection = () => {
        // Only update region if user has opened the menu
        if (menuOpen) {
            update(selectedRegion);
        }
        toggleDropdownVisibility();
    };

    // Renders list of radio buttons with regions
    const regions = REGIONS_LIST.map((region, index) => {
        if (region.hideInNav) return;

        const regionProps = {
            key: `regionItem-${index}`,
            "data-preference": region.preference,
            /* Use `trendingsection` as React does not recognize the
            camelCase `data-trendingSection` prop on a DOM element */
            "data-trendingsection": region.trendingSection,
            className: "regionItemLabel",
            onClick: handleRegionSelection,
        };

        const htmlID = `region-${region.id}${placement}`;

        return (
            <li className="regionsListItem" key={`regionLink-${index}`}>
                <label
                    {...regionProps}
                    htmlFor={htmlID}
                    data-testid="regionItemLabel"
                >
                    <input
                        type="radio"
                        name="region"
                        value={region.title}
                        className="regionRadioButton"
                        id={htmlID}
                        defaultChecked={region.preference === selectedRegion}
                    />
                    {region.title}
                </label>
            </li>
        );
    });

    const wrapperClasses = classNames("regionsListWrapper", className, {
        openRegion: menuOpen,
        standAlone: placement === CONSTANTS.PLACEMENT.SIDEBAR,
    });

    const ariaRoles = menuOpen
        ? CONSTANTS.ARIA_MENU.active
        : CONSTANTS.ARIA_MENU.inactive;

    const heading =
        placement === `sidebar` ? (
            <Heading text="My Local" level="h2" levelClassName="h3" />
        ) : null;

    const buttonLabel = menuOpen
        ? "Update Preference"
        : useDefault || isExplicitlySet
        ? "Change"
        : CONSTANTS.SET_YOUR_LOCAL_BUTTON_LABEL;

    return (
        <div className={wrapperClasses} data-testid="regions-list-selector">
            <div className="regionNameWrapper">
                {heading}
                <LinkWrap
                    className="regionLink currentRegion sclt-currentRegionLink"
                    data-cy="current-region"
                    url={`/${region.path}`}
                    external={false}
                    id={`current-local-${placement}`}
                    aria-label={`Show news for my current region: ${region.title}`}
                >
                    {region.title}
                    <IconChevron className="horizontal" />
                </LinkWrap>
            </div>
            <button
                className="regionButton sclt-regionChangeButton"
                data-cy="region-button"
                aria-controls={`regionsList-${placement}`}
                aria-expanded={menuOpen}
                aria-describedby={`about-region-change-${placement}`}
                onClick={handleConfirmSelection}
            >
                {buttonLabel}
            </button>
            <span className="a11y" id={`about-region-change-${placement}`}>
                quickly access local content from your selected region
            </span>
            <form className="regionForm" action="">
                <span className="regionFormIntro">
                    Select a new default local region:
                </span>
                <ul
                    id={`regionsList-${placement}`}
                    className="regionsList"
                    data-cy="regions-list"
                    {...ariaRoles}
                >
                    {regions}
                </ul>
            </form>
        </div>
    );
};

RegionsListSelector.propTypes = {
    className: PropTypes.string,
    placement: PropTypes.string,
    useDefault: PropTypes.bool,
};

export default RegionsListSelector;
